import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Users,
  Layout,
  Code,
  Shield,
  Globe,
  PieChart,
} from "lucide-react";

const ProcessSection = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
    });
  }, []);

const whatWeOffer = [
  {
    icon: Globe,
    title: "Custom API Development",
    description:
      "Build secure, scalable, and high-performance custom APIs that enable smooth communication between systems and applications.",
  },
  {
    icon: Layout,
    title: "Third-Party API Integration",
    description:
      "Effortlessly integrate payment gateways, CRMs, ERPs, SMS/Email APIs, cloud platforms, and other third-party services.",
  },
  {
    icon: Users,
    title: "Workflow & Process Automation",
    description:
      "Automate repetitive tasks, business operations, and backend workflows to save time and reduce manual effort.",
  },
  {
    icon: PieChart,
    title: "Data Sync & Real-Time Communication",
    description:
      "Enable automated syncing, live data exchange, event-based updates, and real-time communication between systems.",
  },
  {
    icon: Code,
    title: "Performance & Security Optimization",
    description:
      "Improve API response times, apply caching, rate limiting, and enforce enterprise-grade security across all integrations.",
  },
  {
    icon: Shield,
    title: "Monitoring, Logs & Ongoing Support",
    description:
      "Get continuous monitoring, logging, debugging, and support to ensure stable, reliable API performance at all times.",
  },
];


const processSteps = [
  {
    number: "01",
    title: "Requirement Analysis & API Planning",
    description:
      "We identify integration needs, analyze systems, map data flow, and design a structured API architecture.",
    icon: Users,
      imagePath: "./images/servicesImages/Requirement Analysis & API Planning.jpg",
  },
  {
    number: "02",
    title: "API Development & Configuration",
    description:
      "We develop APIs, set up authentication, create endpoints, and configure automation logic with clean documentation.",
    icon: Layout,
      imagePath: "./images/servicesImages/API Development & Configuration.jpg",
  },
  {
    number: "03",
    title: "Automation Setup & Testing",
    description:
      "We configure automation workflows, test communication between systems, validate data accuracy, and ensure secure performance.",
    icon: Code,
      imagePath: "./images/servicesImages/Automation Setup & Testing.avif",
  },
  {
    number: "04",
    title: "Deployment, Monitoring & Support",
    description:
      "APIs are deployed, monitored in real time, optimized for speed, and supported with logs, alerts, and continuous maintenance.",
    icon: Shield,
      imagePath: "./images/servicesImages/Deployment, Monitoring & Support.avif",
  },
];


const ourEdge = [
  "🔗 Seamless System Connectivity: Unified integration across apps, databases, and platforms for smooth digital operations.",
  "⚡ High-Performance APIs: Fast, scalable, low-latency integrations built for modern, high-traffic environments.",
  "🔐 Enterprise-Grade Security: Secure authentication, encryption, token handling, and compliance-ready frameworks.",
  "🤖 Automation-Driven Approach: Reduce manual work and boost efficiency with fully automated, event-based workflows.",
];


const howWeWork = [
  "1. Requirement Study – We analyze integration points, data flow, and automation needs to build a clear plan.",
  "2. API Design & Development – Secure APIs are designed, developed, and connected with third-party platforms.",
  "3. Automation Implementation – Automated triggers, schedules, and workflows are configured and thoroughly tested.",
  "4. Monitoring & Support – We track API performance, resolve issues, optimize speed, and ensure long-term reliability.",
];



  return (
    <section className="py-20 px-[5%] bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* What We Offer Section */}
        <div className="mb-28">
          <h2
            className="text-center text-3xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-12"
            data-aos="fade-up"
          >
            What We Offer
          </h2>
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {whatWeOffer.map((feature, index) => (
              <div
                key={index}
                className="bg-[#dcfaf9] rounded-full p-8 shadow-md border-t-4 border-b-4 border-[#0d6b68] hover:shadow-xl transition-all duration-300"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="flex items-center gap-4 mb-4">
                  <feature.icon className="w-8 h-8 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-32" data-aos="fade-up">
  <h2 className="text-4xl md:text-6xl font-semibold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-6">
    Our Process
  </h2>

  <p className="text-gray-600 text-lg sm:text-xl max-w-2xl mx-auto">
    We follow a clear, structured process to ensure seamless API integration and
    smooth automation across all your systems. From planning and development to
    testing and deployment, every step is optimized for reliability, security,
    and real-time performance.
  </p>
</div>


        {/* Process Grid */}
        <div className="grid md:grid-cols-2 gap-y-32 gap-x-10">
          {processSteps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div
                key={index}
                className="flex flex-col relative"
                data-aos="fade-up"
                data-aos-delay={index * 200}
              >
                {/* Background Number */}
                <div className="absolute -top-24 text-6xl md:text-9xl font-bold text-[#9ee8e7] select-none pointer-events-none z-0">
                  {step.number}
                </div>

                {/* Card */}
                <div className="bg-gradient-to-br from-[#0d6b68] to-[#baf7f6] rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 z-10 border border-gray-100">
                  {/* Card Image/Icon Area */}
                  <div className="mb-8 relative">
                    <div className="w-full h-fit bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl flex items-center justify-center overflow-hidden relative">
                      <img
                        src={step.imagePath}
                        alt={step.title}
                        className="w-full h-38 md:h-40 lg:h-52 object-cover rounded-2xl"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextElementSibling.style.display = "flex";
                        }}
                      />

                      {/* Fallback Icon */}
                      <div className="absolute inset-0 hidden items-center justify-center">
                        <div className="relative">
                          <div className="absolute -top-4 -left-4 w-3 h-3 bg-blue-400 rounded-full opacity-60"></div>
                          <div className="absolute -bottom-6 -right-6 w-2 h-2 bg-purple-400 rounded-full opacity-40"></div>
                          <div className="absolute top-2 right-4 w-1.5 h-1.5 bg-indigo-400 rounded-full opacity-50"></div>
                          <div className="bg-white p-6 rounded-2xl shadow-lg">
                            <IconComponent className="w-12 h-12 text-blue-600" />
                          </div>
                          <div className="absolute top-1/2 -left-8 w-6 h-0.5 bg-blue-300 opacity-60"></div>
                          <div className="absolute top-1/2 -right-8 w-6 h-0.5 bg-blue-300 opacity-60"></div>
                          <div className="absolute -top-8 left-1/2 w-0.5 h-6 bg-blue-300 opacity-60"></div>
                          <div className="absolute -bottom-8 left-1/2 w-0.5 h-6 bg-blue-300 opacity-60"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Our Edge Section */}
        <div className="text-center mt-32" data-aos="fade-up">
          <h2
            className="text-4xl md:text-6xl font-semibold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-8"
            style={{ lineHeight: "1.4" }}
          >
            Our Edge
          </h2>

          <ul className="text-gray-700 max-w-4xl mx-auto space-y-4 text-lg">
            {ourEdge.map((point, index) => (
              <li key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                {point}
              </li>
            ))}
          </ul>
        </div>

        {/* How We Work Section */}
        <div className="text-center mt-20" data-aos="fade-up">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-8 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            How We Work
          </h2>
          <ul className="text-gray-700 max-w-3xl mx-auto space-y-4 text-lg">
            {howWeWork.map((step, index) => (
              <li key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                {step}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
